import { ErrorHandler } from '@angular/core';
import type { ApplicationConfig } from '@angular/core';
import type { Type } from '@angular/core';
import { FilterState, SimpleActiveFilterService } from '@ui/shared/domain-filter';
import {
    AbstractMapIconsService,
    AbstractSvgIconService,
    MapIconsService,
    SvgIconService,
} from '@ui/shared/domain-api';
import { AbstractEnvironmentService, EnvironmentService, provideConfigModule } from '@ui/shared/domain-config';
import { apiConfigurationFactory, Configuration } from '@ui/shared/util-api';
import { provideTheme } from '@ui/theme';
import { MapItemService } from '@ui/shared/feature-map';
import { provideMaplibreWorker } from '@maplibre/ngx-maplibre-gl/config';
import type { MapDataService } from '@ui/shared/domain-map';
import { MapDataServices, MapDataState } from '@ui/shared/domain-map';
import type { FilterConfigService } from '@ui/shared/util-filter';
import { FilterConfigToken } from '@ui/shared/util-filter';
import { bugsnagErrorHandlerFactory } from './bugsnag-error-handler.factory';

/**
 * configure your app with injectables to do all your magic
 */
export function createAppConfig<Item, Filter = unknown>({ mapDataStateService, filterItems = [], dataEndpoints = [] }: {
    mapDataStateService: Type<MapDataState<Item>>;
    filterItems?: Array<Type<FilterConfigService<keyof Filter extends string ? keyof Filter : never>>>;
    dataEndpoints?: Array<Type<MapDataService<Item, Filter>>>;
}): ApplicationConfig {
    return {
        providers: [
            ...filterItems.map(item => ({
                provide: FilterConfigToken,
                useExisting: item,
                multi: true,
            })),
            ...dataEndpoints.map(item => ({
                provide: MapDataServices,
                useExisting: item,
                multi: true,
            })),
            MapItemService,
            {
                provide: MapDataState,
                useExisting: mapDataStateService,
            },
            ...provideConfigModule(),
            {
                provide: FilterState,
                useClass: SimpleActiveFilterService,
            },
            {
                provide: AbstractMapIconsService,
                useClass: MapIconsService,
            },
            {
                provide: AbstractSvgIconService,
                useClass: SvgIconService,
            },
            {
                provide: AbstractEnvironmentService,
                useClass: EnvironmentService,
            },
            {
                provide: Configuration,
                useFactory: apiConfigurationFactory,
                deps: [AbstractEnvironmentService],
            },
            {
                provide: ErrorHandler,
                useFactory: bugsnagErrorHandlerFactory,
            },
            provideTheme(),
            provideMaplibreWorker('maplibre-gl-worker.mjs'),
        ],
    };
}
